import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  HStack,
  Input,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react'
import { useAuth } from '@hb/react'
import {
  CordBloodBankingDecision,
  CordBloodBankingRecord,
  CordBloodGasRecord,
  CordBloodSampleType,
} from '@hb/shared'
import { useCallback } from 'react'

// ─── Shared ───────────────────────────────────────────────────────────────────

function RadioGroup<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: T | undefined
  options: { id: T; text: string }[]
  onChange: (v: T) => void
}) {
  return (
    <Flex direction="column" gap={2}>
      <Text
        fontFamily="Hero-New"
        fontWeight={600}
        fontSize="sm"
        color="gray.700"
        _dark={{ color: 'gray.200' }}>
        {label}
      </Text>
      <Flex gap={2} flexWrap="wrap">
        {options.map(opt => (
          <Button
            key={opt.id}
            size="sm"
            fontFamily="Hero-New"
            colorScheme={value === opt.id ? 'pink' : 'gray'}
            variant={value === opt.id ? 'solid' : 'outline'}
            onClick={() => onChange(opt.id)}>
            {opt.text}
          </Button>
        ))}
      </Flex>
    </Flex>
  )
}

// ─── Cord Blood Banking ───────────────────────────────────────────────────────

interface BankingProps {
  record: CordBloodBankingRecord
  onChange: (r: CordBloodBankingRecord) => void
}

export const CordBloodBankingSubsection = ({ record, onChange }: BankingProps) => {
  const set = <K extends keyof CordBloodBankingRecord>(k: K, v: CordBloodBankingRecord[K]) =>
    onChange({ ...record, [k]: v })

  const bankingElected = record.bankingDecision === 'elected'

  return (
    <VStack align="stretch" spacing={4}>
      <RadioGroup<CordBloodBankingDecision>
        label="Banking Decision"
        value={record.bankingDecision}
        onChange={v => set('bankingDecision', v)}
        options={[
          { id: 'elected', text: 'Patient Elected Banking' },
          { id: 'declined', text: 'Declined' },
          { id: 'undecided', text: 'Not Decided' },
        ]}
      />

      {bankingElected && (
        <>
          <Checkbox
            isChecked={!!record.collectionKitPresent}
            onChange={e => set('collectionKitPresent', e.target.checked)}
            colorScheme="pink"
            fontFamily="Hero-New"
            fontSize="sm">
            Cord blood banking collection kit confirmed present
            <Text as="span" color="red.400" ml={1}>
              *
            </Text>
          </Checkbox>

          {!record.collectionKitPresent && (
            <Alert status="warning" borderRadius={6} py={2} fontSize="xs">
              <AlertIcon boxSize={3} />
              Collection kit must be present before proceeding with banking documentation.
            </Alert>
          )}

          {record.collectionKitPresent && (
            <>
              <RadioGroup<'yes' | 'no'>
                label="Collection Performed"
                value={
                  record.collectionPerformed == null
                    ? undefined
                    : record.collectionPerformed
                      ? 'yes'
                      : 'no'
                }
                onChange={v => set('collectionPerformed', v === 'yes')}
                options={[
                  { id: 'yes', text: 'Yes' },
                  { id: 'no', text: 'No' },
                ]}
              />
              {record.collectionPerformed === false && (
                <Flex direction="column" gap={1}>
                  <Text
                    fontFamily="Hero-New"
                    fontSize="sm"
                    fontWeight={600}
                    color="gray.700"
                    _dark={{ color: 'gray.200' }}>
                    Reason Collection Not Performed{' '}
                    <Text as="span" color="red.400">
                      *
                    </Text>
                  </Text>
                  <Textarea
                    value={record.collectionNotPerformedReason ?? ''}
                    onChange={e => set('collectionNotPerformedReason', e.target.value)}
                    placeholder="e.g. insufficient blood volume, cord clamped before collection, patient withdrew consent…"
                    size="sm"
                    fontFamily="Hero-New"
                    rows={2}
                  />
                </Flex>
              )}

              {record.collectionPerformed && (
                <>
                  <Flex direction="column" gap={1}>
                    <Text
                      fontFamily="Hero-New"
                      fontWeight={600}
                      fontSize="sm"
                      color="gray.700"
                      _dark={{ color: 'gray.200' }}>
                      Banking Company Name
                    </Text>
                    <Input
                      value={record.bankingCompanyName ?? ''}
                      onChange={e => set('bankingCompanyName', e.target.value)}
                      size="sm"
                      fontFamily="Hero-New"
                      placeholder="e.g. Cord Blood Registry"
                      maxW="280px"
                    />
                  </Flex>
                  <Flex direction="column" gap={1}>
                    <Text
                      fontFamily="Hero-New"
                      fontWeight={600}
                      fontSize="sm"
                      color="gray.700"
                      _dark={{ color: 'gray.200' }}>
                      Collection Time
                    </Text>
                    <Input
                      type="datetime-local"
                      value={
                        record.collectionTime
                          ? new Date(record.collectionTime).toISOString().slice(0, 16)
                          : ''
                      }
                      onChange={e => {
                        const v = e.target.value ? new Date(e.target.value).getTime() : undefined
                        set('collectionTime', v)
                      }}
                      size="sm"
                      fontFamily="Hero-New"
                      maxW="260px"
                    />
                  </Flex>
                  <Checkbox
                    isChecked={!!record.chainOfCustodyConfirmed}
                    onChange={e => set('chainOfCustodyConfirmed', e.target.checked)}
                    colorScheme="pink"
                    fontFamily="Hero-New"
                    fontSize="sm">
                    Collection kit labeled, sealed, and handed to courier / courier contacted
                  </Checkbox>
                </>
              )}
            </>
          )}
        </>
      )}
    </VStack>
  )
}

// ─── Cord Blood Gases ─────────────────────────────────────────────────────────

interface GasProps {
  record: CordBloodGasRecord
  onChange: (r: CordBloodGasRecord) => void
}

const numericGasField = (
  label: string,
  value: number | undefined,
  onChange: (v: number | undefined) => void,
  unit?: string,
  flagComponent?: React.ReactNode,
) => (
  <Flex direction="column" gap={1} key={label}>
    <Text
      fontFamily="Hero-New"
      fontWeight={600}
      fontSize="sm"
      color="gray.700"
      _dark={{ color: 'gray.200' }}>
      {label}
    </Text>
    <HStack>
      <NumberInput
        min={-99}
        max={999}
        step={0.01}
        precision={2}
        value={value ?? ''}
        onChange={(_, n) => onChange(isNaN(n) ? undefined : n)}
        size="sm"
        maxW="120px">
        <NumberInputField fontFamily="Hero-New" />
        <NumberInputStepper>
          <NumberIncrementStepper />
          <NumberDecrementStepper />
        </NumberInputStepper>
      </NumberInput>
      {unit && (
        <Text fontSize="sm" color="gray.500" fontFamily="Hero-New">
          {unit}
        </Text>
      )}
    </HStack>
    {flagComponent}
  </Flex>
)

export const CordBloodGasSubsection = ({ record, onChange }: GasProps) => {
  const { authUser } = useAuth()
  const set = <K extends keyof CordBloodGasRecord>(k: K, v: CordBloodGasRecord[K]) =>
    onChange({ ...record, [k]: v })

  const handleAcknowledge = useCallback(() => {
    onChange({
      ...record,
      acknowledgedAt: Date.now(),
      acknowledgedBy: authUser?.uid,
    })
  }, [record, onChange, authUser])

  const criticalPh = record.ph != null && record.ph < 7.0
  const lowPh = record.ph != null && record.ph >= 7.0 && record.ph < 7.2
  const lowBe = record.baseExcess != null && record.baseExcess < -10

  return (
    <VStack align="stretch" spacing={4}>
      <Checkbox
        isChecked={!!record.ordered}
        onChange={e => set('ordered', e.target.checked)}
        colorScheme="pink"
        fontFamily="Hero-New"
        fontSize="sm">
        Cord blood gases ordered
      </Checkbox>

      {record.ordered && (
        <>
          <Flex direction="column" gap={1}>
            <Text
              fontFamily="Hero-New"
              fontWeight={600}
              fontSize="sm"
              color="gray.700"
              _dark={{ color: 'gray.200' }}>
              Indication{' '}
              <Text as="span" color="red.400">
                *
              </Text>
            </Text>
            <Textarea
              value={record.indication ?? ''}
              onChange={e => set('indication', e.target.value)}
              placeholder="Clinical reason for ordering cord blood gases…"
              size="sm"
              fontFamily="Hero-New"
              rows={2}
            />
          </Flex>

          <RadioGroup<CordBloodSampleType>
            label="Sample Type"
            value={record.sampleType}
            onChange={v => set('sampleType', v)}
            options={[
              { id: 'arterial', text: 'Arterial' },
              { id: 'venous', text: 'Venous' },
              { id: 'both', text: 'Both' },
            ]}
          />

          {numericGasField(
            'pH',
            record.ph,
            v => set('ph', v),
            undefined,
            <>
              {criticalPh && (
                <Alert status="error" borderRadius={4} py={1} fontSize="xs" mt={1}>
                  <AlertIcon boxSize={3} />
                  pH &lt;7.00 — SEVERE ACIDOSIS. Neonatal emergency — immediate evaluation required.
                </Alert>
              )}
              {lowPh && (
                <Alert status="warning" borderRadius={4} py={1} fontSize="xs" mt={1}>
                  <AlertIcon boxSize={3} />
                  pH &lt;7.20 — acidosis. Neonatal clinical review required.
                </Alert>
              )}
            </>,
          )}
          {numericGasField(
            'pCO2',
            record.pco2,
            v => set('pco2', v),
            'mmHg',
            record.pco2 != null && (record.pco2 < 35 || record.pco2 > 65) ? (
              <Alert status="warning" borderRadius={4} py={1} fontSize="xs" mt={1}>
                <AlertIcon boxSize={3} />
                pCO2 outside normal range (35–65 mmHg).
              </Alert>
            ) : null,
          )}
          {numericGasField('pO2', record.po2, v => set('po2', v), 'mmHg')}
          {numericGasField(
            'Base Excess',
            record.baseExcess,
            v => set('baseExcess', v),
            'mEq/L',
            lowBe ? (
              <Alert status="warning" borderRadius={4} py={1} fontSize="xs" mt={1}>
                <AlertIcon boxSize={3} />
                Base excess &lt;−10 — metabolic acidosis.
              </Alert>
            ) : null,
          )}
          {numericGasField('Bicarbonate', record.bicarbonate, v => set('bicarbonate', v), 'mEq/L')}

          <Divider />

          {record.acknowledgedAt ? (
            <HStack>
              <Badge colorScheme="green" fontFamily="Hero-New">
                Reviewed and Acknowledged
              </Badge>
              <Text fontSize="xs" color="gray.500" fontFamily="Hero-New">
                {new Date(record.acknowledgedAt).toLocaleString()}
              </Text>
            </HStack>
          ) : (
            <Box>
              <Alert status="warning" borderRadius={6} py={2} fontSize="xs" mb={2}>
                <AlertIcon boxSize={3} />
                Provider must review and acknowledge cord blood gas results before neonatal record
                can be closed.
              </Alert>
              <Button
                size="sm"
                colorScheme="green"
                fontFamily="Hero-New"
                isDisabled={!record.ph}
                onClick={handleAcknowledge}>
                Reviewed and Acknowledged
              </Button>
            </Box>
          )}
        </>
      )}
    </VStack>
  )
}
